/**
 * A WEB APP MANIFEST FOR THE PAGE YOU ARE ON.
 *
 * Two platforms want opposite things from one static file, and this is how
 * both get what they need.
 *
 * iOS takes "Add to Home Screen" from the manifest's `start_url` once a
 * manifest exists — so a fixed `start_url: "/"` means every icon, from every
 * page, opens the Fias root. That is what made it impossible to put Flappyland
 * itself on a home screen (Tom, 2026-09-10: "it removes the Flappyland from
 * the URL").
 *
 * Dropping `start_url` fixed iOS, because the spec then defaults it to the
 * document URL. But Chrome REQUIRES it, and dropping it silently cost Android
 * its install prompt. Measured, not assumed — `Page.getInstallabilityErrors`
 * on deployed staging returned `start-url-not-valid`
 * (`scripts/check-installability.mjs` reproduces it).
 *
 * A manifest whose `start_url` IS the current page satisfies both: Chrome gets
 * a valid `start_url`, and the icon opens the page it was added from either
 * way. On a static site that has to be generated at runtime, and the delivery
 * mechanism is not a free choice — Chrome rejects a `blob:` manifest
 * (`start-url-not-valid`, since relative URLs have no base inside one) and
 * accepts a `data:` manifest. Both were measured with the same probe.
 *
 * Every URL emitted here is therefore ABSOLUTE. Inside a `data:` URL there is
 * no sensible base to resolve a relative path against, and an absolute one
 * removes the question.
 *
 * No `id` field, deliberately: without one a manifest's identity IS its
 * `start_url`, so each page installs as its own app rather than replacing the
 * last. That is the point — Flappyland gets its own icon.
 *
 * An arche page also passes an IDENTITY (its name and its icon), because an
 * icon added from Flappyland should say Flappyland and show Flappyland's art,
 * not be a second copy of Fias. Everything in this module stays a pure
 * function of its arguments; who is on screen is tracked next to the component
 * that writes the DOM, in `components/PageManifestLink.tsx`.
 */

/**
 * Everything about the manifest that does NOT depend on the page.
 *
 * Duplicated from `public/manifest.webmanifest`, which is SAFARI'S copy: it
 * fetches the manifest once during page load and never re-reads, so the static
 * file is the only one it ever sees. That file deliberately omits `start_url`
 * so Safari falls back to the document URL; Chrome, which re-reads the live
 * link, gets its `start_url` from the `data:` URL built below.
 * `page-manifest.test.ts` pins the two together on every other field — the
 * risk of any deliberate duplication is drift.
 */
export const PAGE_MANIFEST_BASE = {
  name: 'Fias',
  short_name: 'Fias',
  description:
    'Fias is a marketplace where AI agents are metered, priced, and paid per invocation.',
  display: 'standalone',
  background_color: '#ffffff',
  theme_color: '#ffffff',
  icons: [
    { src: '/fias-logo-image.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
    { src: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png', purpose: 'any' },
    { src: '/favicon-32x32.png', sizes: '32x32', type: 'image/png', purpose: 'any' },
  ],
} as const;

/** One icon, shaped as the manifest wants it. */
export interface PageManifestIcon {
  src: string;
  sizes: string;
  type?: string;
}

/**
 * Who the current page IS, when it is something more specific than Fias.
 *
 * `icons` may be empty — an arche with a name but no uploaded icon still gets
 * its name on the home screen, and keeps the Fias artwork.
 */
export interface PageManifestIdentity {
  name: string;
  icons: PageManifestIcon[];
}

/**
 * The manifest for one page, as a plain object.
 *
 * `pathname` only — no query, no hash. A home-screen icon is a durable thing
 * and should not carry a one-off `?tab=` or an auth callback's `?code=`; those
 * would install a URL that means nothing tomorrow, and in the auth case would
 * bake a spent credential into an icon.
 */
export function buildPageManifest(
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): Record<string, unknown> {
  // `new URL` leaves an already-absolute URL alone, so arche icons (which come
  // from the icon CDN, a different origin) pass through untouched.
  const abs = (p: string) => new URL(p, origin).toString();
  const icons = identity?.icons.length ? identity.icons : PAGE_MANIFEST_BASE.icons;
  return {
    ...PAGE_MANIFEST_BASE,
    ...(identity ? { name: identity.name, short_name: identity.name } : {}),
    start_url: abs(pathname || '/'),
    scope: abs('/'),
    icons: icons.map((i) => ({ ...i, src: abs(i.src) })),
  };
}

/** The `data:` URL a `<link rel="manifest">` points at for this page. */
export function pageManifestHref(
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): string {
  const json = JSON.stringify(buildPageManifest(origin, pathname, identity));
  return `data:application/manifest+json,${encodeURIComponent(json)}`;
}

/**
 * Point the document's manifest link at this page. Returns false when there is
 * no link to point (nothing to do, and nothing to throw about).
 *
 * Idempotent: re-running with the same path rewrites the same href.
 */
export function applyPageManifest(
  doc: Document,
  origin: string,
  pathname: string,
  identity?: PageManifestIdentity | null,
): boolean {
  const link = doc.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) return false;
  link.href = pageManifestHref(origin, pathname, identity);
  return true;
}

/**
 * The size iOS asks for in `<link rel="apple-touch-icon">`.
 *
 * iOS takes the home-screen icon from this tag, NOT from the manifest, so the
 * manifest work above does nothing for it on its own. There is no 180 variant
 * to offer — the icon pipeline writes 64/128/256/512 — so the rule is the
 * smallest one that is at least this big, which iOS scales down cleanly.
 */
const APPLE_TOUCH_ICON_PX = 180;

/** The first icon at least `APPLE_TOUCH_ICON_PX` square, smallest first. */
export function pickAppleTouchIcon(icons: PageManifestIcon[]): PageManifestIcon | null {
  const sized = icons
    .map((icon) => ({ icon, px: Number.parseInt(icon.sizes.split('x')[0] ?? '', 10) }))
    .filter((c) => Number.isFinite(c.px) && c.px >= APPLE_TOUCH_ICON_PX)
    .sort((a, b) => a.px - b.px);
  return sized[0]?.icon ?? null;
}

/**
 * Each document's own apple-touch-icon, remembered the first time we replace
 * it so leaving an arche restores exactly what `index.html` shipped rather
 * than a path repeated here (which would rot the moment that file changes).
 *
 * Keyed by Document rather than held in one variable: there is only ever one
 * document in the app, but a module-level value would leak between tests and
 * make the first test to run decide what every later one restores to.
 */
const platformAppleTouchIcons = new WeakMap<Document, { href: string; sizes: string }>();

/**
 * Point `<link rel="apple-touch-icon">` at this page's icon, or back at the
 * platform's when `identity` is null. Returns false when there is no such tag.
 */
export function applyAppleTouchIcon(
  doc: Document,
  identity?: PageManifestIdentity | null,
): boolean {
  const link = doc.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (!link) return false;
  let platform = platformAppleTouchIcons.get(doc);
  if (!platform) {
    platform = {
      href: link.getAttribute('href') ?? '',
      sizes: link.getAttribute('sizes') ?? '',
    };
    platformAppleTouchIcons.set(doc, platform);
  }
  const chosen = identity ? pickAppleTouchIcon(identity.icons) : null;
  link.setAttribute('href', chosen ? chosen.src : platform.href);
  // The `sizes` attribute must keep describing the file it points at.
  if (chosen) {
    link.setAttribute('sizes', chosen.sizes);
  } else if (platform.sizes) {
    link.setAttribute('sizes', platform.sizes);
  } else {
    link.removeAttribute('sizes');
  }
  return true;
}
